/**
 * MarkerConfigLoader
 *
 * Loads and validates marker configuration files for multi-marker AR experiences.
 * Handles JSON parsing, schema validation, and file existence checks.
 */

export class ConfigNotFoundError extends Error {
  constructor(configPath) {
    super(`Configuration file not found: ${configPath}`);
    this.name = 'ConfigNotFoundError';
    this.configPath = configPath;
  }
}

export class ConfigParseError extends Error {
  constructor(configPath, cause) {
    super(`Failed to parse configuration file: ${configPath}`);
    this.name = 'ConfigParseError';
    this.configPath = configPath;
    this.cause = cause;
  }
}

export class ConfigValidationError extends Error {
  constructor(errors) {
    super(`Configuration validation failed: ${errors.length} error(s)`);
    this.name = 'ConfigValidationError';
    this.errors = errors;
  }
}

export class MarkerConfigLoader {
  /**
   * Load configuration from JSON file or posterId
   * @param {string} configPathOrId - Path to config file OR posterId (e.g., /configs/poster-001-config.json OR "multi-marker-demo")
   * @returns {Promise<Object>} Parsed and validated MarkerConfig
   * @throws {ConfigNotFoundError} If file not found
   * @throws {ConfigParseError} If JSON parsing fails
   * @throws {ConfigValidationError} If validation fails
   */
  static async load(configPathOrId) {
    // Check if it's a path (starts with /) or a posterId
    if (configPathOrId.startsWith('/')) {
      return this._loadFromPath(configPathOrId);
    } else {
      return this._loadFromPosterId(configPathOrId);
    }
  }

  /**
   * Load configuration from a direct path (legacy method)
   * @private
   */
  static async _loadFromPath(configPath) {
    try {
      const response = await fetch(configPath);

      if (!response.ok) {
        if (response.status === 404) {
          throw new ConfigNotFoundError(configPath);
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const config = await response.json();

      // Validate the configuration
      const validationResult = this.validate(config);
      if (!validationResult.isValid) {
        throw new ConfigValidationError(validationResult.errors);
      }

      return config;
    } catch (error) {
      if (error instanceof ConfigNotFoundError || error instanceof ConfigValidationError) {
        throw error;
      }
      if (error instanceof SyntaxError) {
        throw new ConfigParseError(configPath, error);
      }
      throw new ConfigParseError(configPath, error);
    }
  }

  /**
   * Load configuration from posters.json using posterId
   * @private
   */
  static async _loadFromPosterId(posterId) {
    const postersJsonPath = '/data/posters.json';

    try {
      const response = await fetch(postersJsonPath);

      if (!response.ok) {
        throw new ConfigNotFoundError(postersJsonPath);
      }

      const posters = await response.json();
      const poster = posters[posterId];

      if (!poster) {
        throw new ConfigNotFoundError(`Poster ID "${posterId}" not found in ${postersJsonPath}`);
      }

      // Check if it's a multi-marker poster
      if (poster.type !== 'multi-marker') {
        throw new ConfigValidationError([
          {
            field: 'type',
            message: `Poster "${posterId}" is not a multi-marker poster (type: ${poster.type || 'single-marker'})`,
            severity: 'error',
          },
        ]);
      }

      // Transform to expected format (targetUrl → mindFilePath)
      const config = {
        posterId: poster.id,
        posterImagePath: poster.posterImagePath,
        mindFilePath: poster.targetUrl, // Map targetUrl to mindFilePath
        aspectRatio: poster.aspectRatio,
        markers: poster.markers,
        debug: poster.debug,
        videoDelivery: poster.videoDelivery, // Cloudflare Stream support
        audioType: poster.audioType, // Audio support (video or wav)
        overlayHtml: poster.overlayHtml, // Global HTML overlay
      };

      // Validate the transformed configuration
      const validationResult = this.validate(config);
      if (!validationResult.isValid) {
        throw new ConfigValidationError(validationResult.errors);
      }

      return config;
    } catch (error) {
      if (error instanceof ConfigNotFoundError || error instanceof ConfigValidationError) {
        throw error;
      }
      if (error instanceof SyntaxError) {
        throw new ConfigParseError(postersJsonPath, error);
      }
      throw new ConfigParseError(postersJsonPath, error);
    }
  }

  /**
   * Validate MarkerConfig structure and constraints
   * @param {Object} config - Configuration object to validate
   * @returns {{isValid: boolean, errors: Array}} Validation result
   */
  static validate(config) {
    const errors = [];

    // Required fields check
    if (!config.posterId) {
      errors.push({
        field: 'posterId',
        message: 'posterId is required',
        severity: 'error',
      });
    } else if (!/^[a-zA-Z0-9-]+$/.test(config.posterId) || config.posterId.length > 50) {
      errors.push({
        field: 'posterId',
        message:
          'posterId must contain only alphanumeric characters and hyphens, max 50 characters',
        severity: 'error',
      });
    }

    if (!config.mindFilePath) {
      errors.push({
        field: 'mindFilePath',
        message: 'mindFilePath is required',
        severity: 'error',
      });
    } else if (!config.mindFilePath.endsWith('.mind')) {
      errors.push({
        field: 'mindFilePath',
        message: 'mindFilePath must have .mind extension',
        severity: 'error',
      });
    }

    if (!config.markers || !Array.isArray(config.markers)) {
      errors.push({
        field: 'markers',
        message: 'markers array is required',
        severity: 'error',
      });
      return { isValid: false, errors };
    }

    if (config.markers.length === 0) {
      errors.push({
        field: 'markers',
        message: 'markers array cannot be empty',
        severity: 'error',
      });
      return { isValid: false, errors };
    }

    if (config.markers.length > 10) {
      errors.push({
        field: 'markers',
        message: 'markers array cannot exceed 10 items (maxTrack limit)',
        severity: 'error',
      });
    }

    // Check for duplicate marker IDs and targetIndexes
    const markerIds = new Set();
    const targetIndexes = new Set();

    config.markers.forEach((marker, index) => {
      const prefix = `markers[${index}]`;

      // ID validation
      if (!marker.id) {
        errors.push({
          field: `${prefix}.id`,
          message: 'id is required',
          severity: 'error',
        });
      } else {
        if (markerIds.has(marker.id)) {
          errors.push({
            field: `${prefix}.id`,
            message: `Duplicate marker id: ${marker.id}`,
            severity: 'error',
          });
        }
        markerIds.add(marker.id);

        if (!/^[a-zA-Z0-9-]+$/.test(marker.id) || marker.id.length > 50) {
          errors.push({
            field: `${prefix}.id`,
            message: 'id must contain only alphanumeric characters and hyphens, max 50 characters',
            severity: 'error',
          });
        }
      }

      // targetIndex validation
      if (marker.targetIndex === undefined || marker.targetIndex === null) {
        errors.push({
          field: `${prefix}.targetIndex`,
          message: 'targetIndex is required',
          severity: 'error',
        });
      } else {
        if (typeof marker.targetIndex !== 'number' || marker.targetIndex < 0) {
          errors.push({
            field: `${prefix}.targetIndex`,
            message: 'targetIndex must be a non-negative integer',
            severity: 'error',
          });
        }
        if (targetIndexes.has(marker.targetIndex)) {
          errors.push({
            field: `${prefix}.targetIndex`,
            message: `Duplicate targetIndex: ${marker.targetIndex}`,
            severity: 'error',
          });
        }
        targetIndexes.add(marker.targetIndex);
      }

      // videoPath validation
      if (!marker.videoPath) {
        errors.push({
          field: `${prefix}.videoPath`,
          message: 'videoPath is required',
          severity: 'error',
        });
      } else {
        const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov'];
        const hasValidExtension = videoExtensions.some((ext) =>
          marker.videoPath.toLowerCase().endsWith(ext)
        );
        if (!hasValidExtension) {
          errors.push({
            field: `${prefix}.videoPath`,
            message: `videoPath must have a valid video extension (${videoExtensions.join(', ')})`,
            severity: 'error',
          });
        }
      }

      // bounds validation
      if (!marker.bounds) {
        errors.push({
          field: `${prefix}.bounds`,
          message: 'bounds is required',
          severity: 'error',
        });
      } else {
        ['x', 'y', 'width', 'height'].forEach((prop) => {
          const value = marker.bounds[prop];
          if (value === undefined || value === null) {
            errors.push({
              field: `${prefix}.bounds.${prop}`,
              message: `${prop} is required`,
              severity: 'error',
            });
          } else if (typeof value !== 'number' || value < 0 || value > 1) {
            errors.push({
              field: `${prefix}.bounds.${prop}`,
              message: `${prop} must be between 0 and 1`,
              severity: 'error',
            });
          }
        });

        if (marker.bounds.width !== undefined && marker.bounds.width <= 0) {
          errors.push({
            field: `${prefix}.bounds.width`,
            message: 'width must be greater than 0',
            severity: 'error',
          });
        }

        if (marker.bounds.height !== undefined && marker.bounds.height <= 0) {
          errors.push({
            field: `${prefix}.bounds.height`,
            message: 'height must be greater than 0',
            severity: 'error',
          });
        }
      }

      // priorityWeight validation (optional)
      if (marker.priorityWeight !== undefined) {
        if (
          typeof marker.priorityWeight !== 'number' ||
          marker.priorityWeight < 0.1 ||
          marker.priorityWeight > 10.0
        ) {
          errors.push({
            field: `${prefix}.priorityWeight`,
            message: 'priorityWeight must be between 0.1 and 10.0',
            severity: 'error',
          });
        }
      }
    });

    // overlayHtml validation (optional, global)
    if (config.overlayHtml) {
      if (typeof config.overlayHtml !== 'string') {
        errors.push({
          field: 'overlayHtml',
          message: 'overlayHtml must be a string',
          severity: 'error',
        });
      }
    }

    // aspectRatio validation
    if (config.aspectRatio !== undefined) {
      if (
        typeof config.aspectRatio !== 'number' ||
        config.aspectRatio < 0.1 ||
        config.aspectRatio > 10.0
      ) {
        errors.push({
          field: 'aspectRatio',
          message: 'aspectRatio must be between 0.1 and 10.0',
          severity: 'error',
        });
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate file paths referenced in the configuration
   * @param {Object} config - Configuration object
   * @returns {Promise<Object>} File validation result
   */
  static async validateFilePaths(config) {
    const result = {
      mindFileExists: false,
      mindFileError: null,
      videos: [],
    };

    // Check mind file
    try {
      const mindResponse = await fetch(config.mindFilePath, { method: 'HEAD' });
      result.mindFileExists = mindResponse.ok;
      if (!mindResponse.ok) {
        result.mindFileError = new Error(`HTTP ${mindResponse.status}`);
      }
    } catch (error) {
      result.mindFileError = error;
    }

    // Check video files in parallel
    const videoChecks = config.markers.map(async (marker) => {
      const videoResult = {
        markerId: marker.id,
        videoPath: marker.videoPath,
        exists: false,
        error: null,
      };

      try {
        const videoResponse = await fetch(marker.videoPath, { method: 'HEAD' });
        videoResult.exists = videoResponse.ok;
        if (!videoResponse.ok) {
          videoResult.error = new Error(`HTTP ${videoResponse.status}`);
        }
      } catch (error) {
        videoResult.error = error;
      }

      return videoResult;
    });

    result.videos = await Promise.all(videoChecks);

    return result;
  }
}
